/**
 * 管理画面用APIクライアント
 */

const API_BASE = "/api/admin";

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({
        message: `サーバーから正しい応答を受け取れませんでした（${response.status}）`,
      }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  if (response.status === 204) return null;
  return response.json();
}

// ==================== ダッシュボード ====================
export async function getDashboardStats(grade?: number | null) {
  const query = new URLSearchParams();
  if (grade != null) query.set("grade", String(grade));
  return fetchWithAuth(
    `/dashboard/stats${query.size > 0 ? `?${query.toString()}` : ""}`,
  );
}

export interface TeacherRankingStudent {
  studentId: number;
  name: string;
  participantCode: string | null;
  groupLabel: string | null;
  classId: number | null;
  className: string | null;
  interviewCount: number;
  totalUsageSeconds: number;
  averageUsageSeconds: number;
  averageScore: number;
  /** 公開サンプルのみ: 患者面接の点が付いた面接の数 */
  scoredInterviewCount?: number;
  lastInterviewAt: string | null;
  scoreTrend: Array<{ interviewId: number; score: number; created: string }>;
}

export interface TeacherRankingResponse {
  scope: "educator" | "public_sample";
  period: { days: number; from: string | null; to: string };
  selectedClassId: number | null;
  selectedGrade: number | null;
  classes: Array<{
    id: number;
    name: string;
    academicYear: number | null;
    grade: number | null;
    section: string | null;
    studentCount: number;
  }>;
  summary: {
    activeStudentCount: number;
    totalInterviewCount: number;
    averageUsageSeconds: number;
    averageScore: number;
  };
  weeklyScoreTrend: Array<{
    weekStart: string;
    average: number;
    evaluatedCount: number;
  }>;
  categoryPerformance: Array<{
    categoryName: string;
    achievementRate: number;
    evaluatedItemCount: number;
  }>;
  students: TeacherRankingStudent[];
  usageTimeBasis: "first_to_last_round";
  participantCodeRange?: { min: number; max: number };
}

export async function getTeacherRanking(
  days: number,
  classId?: number | null,
  grade?: number | null,
): Promise<TeacherRankingResponse> {
  const query = new URLSearchParams({ days: String(days) });
  if (classId != null) query.set("classId", String(classId));
  if (grade != null) query.set("grade", String(grade));
  return fetchWithAuth(`/dashboard/ranking?${query.toString()}`);
}

export async function getPublicRankingSample(
  days: number,
  classId?: number | null,
  grade?: number | null,
): Promise<TeacherRankingResponse> {
  const query = new URLSearchParams({ days: String(days) });
  if (classId != null) query.set("classId", String(classId));
  if (grade != null) query.set("grade", String(grade));
  const response = await fetch(
    `/api/public/ranking-sample?${query.toString()}`,
    { credentials: "omit" },
  );
  if (!response.ok) {
    throw new Error("公開ランキングを読み込めませんでした。");
  }
  return response.json();
}

export interface PublicStudentScoreSampleResponse {
  scope: "public_sample";
  student: {
    name: string;
    participantCode: string;
    classId: number | null;
    className: string | null;
  };
  summary: {
    evaluatedCount: number;
    averageScore: number;
    latestScore: number | null;
  };
  scores: Array<{
    interviewId: number;
    created: string;
    patientScore: number | null;
    dentistScore: number | null;
    dentistSkipped: boolean;
    overallScore: number | null;
  }>;
}

export async function getPublicStudentScoreSample(
  participantCode: string,
): Promise<PublicStudentScoreSampleResponse> {
  const response = await fetch(
    `/api/public/ranking-sample/students/${encodeURIComponent(participantCode)}`,
    { credentials: "omit" },
  );
  if (!response.ok) {
    throw new Error("参加者のスコアを読み込めませんでした。");
  }
  return response.json();
}

// ==================== ユーザー管理 ====================
export async function getUsers(params?: {
  search?: string;
  role?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.role) queryParams.append("role", params.role);
  if (params?.is_active !== undefined)
    queryParams.append("is_active", params.is_active.toString());
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return fetchWithAuth(`/users${query ? `?${query}` : ""}`);
}

export async function getUser(id: number) {
  return fetchWithAuth(`/users/${id}`);
}

export interface AssignedStudent {
  id: number;
  name: string;
  email: string;
  participant_code: string | null;
  group_label: string | null;
  class_id: number | null;
  class_name: string | null;
  class_academic_year: number | null;
  class_grade: number | null;
  class_section: string | null;
  interview_count: number;
  scored_interview_count: number;
  last_interview_at: string | null;
}

export async function getAssignedStudents(): Promise<{
  students: AssignedStudent[];
}> {
  return fetchWithAuth("/users/assigned-students");
}

export async function getAssignedStudent(
  id: number,
): Promise<{ student: AssignedStudent }> {
  return fetchWithAuth(`/users/assigned-students/${id}`);
}

export async function createUser(data: {
  name: string;
  email: string;
  role: string;
}) {
  return fetchWithAuth("/users", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateUser(
  id: number,
  data: { name?: string; email?: string; role?: string; is_active?: boolean },
) {
  return fetchWithAuth(`/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/** 有効/無効を切り替える。削除と違い面接記録は残る */
export async function toggleUserActive(id: number) {
  return fetchWithAuth(`/users/${id}/toggle-active`, {
    method: "PATCH",
  });
}

export async function deleteUser(id: number) {
  return fetchWithAuth(`/users/${id}`, {
    method: "DELETE",
  });
}

export type GuestPassStatus = "active" | "expired" | "revoked";

export interface GuestPass {
  id: number;
  label: string;
  expiresAt: string;
  revokedAt: string | null;
  createdBy: number | null;
  createdByName?: string | null;
  created: string;
  entryCount: number;
  status: GuestPassStatus;
}

export async function getGuestPasses(): Promise<{ passes: GuestPass[] }> {
  return fetchWithAuth("/guest-passes");
}

export async function createGuestPass(data: {
  label: string;
  expiresAt: string;
}): Promise<{ pass: GuestPass; token: string; url: string }> {
  return fetchWithAuth("/guest-passes", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function revokeGuestPass(id: number): Promise<{ pass: GuestPass }> {
  return fetchWithAuth(`/guest-passes/${id}/revoke`, {
    method: "POST",
  });
}

// ==================== アバター管理 ====================
export async function getAvatars(params?: {
  search?: string;
  gender?: string;
  ageMin?: number;
  ageMax?: number;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.gender) queryParams.append("gender", params.gender);
  if (params?.ageMin) queryParams.append("ageMin", params.ageMin.toString());
  if (params?.ageMax) queryParams.append("ageMax", params.ageMax.toString());
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return fetchWithAuth(`/avatars${query ? `?${query}` : ""}`);
}

export async function getAvatar(id: number) {
  return fetchWithAuth(`/avatars/${id}`);
}

export async function createAvatar(data: {
  name: string;
  profile_data: any;
  avatar_image_url?: string;
}) {
  return fetchWithAuth("/avatars", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAvatar(
  id: number,
  data: {
    name?: string;
    profile_data?: any;
    avatar_image_url?: string;
    version?: number;
  },
) {
  return fetchWithAuth(`/avatars/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAvatar(id: number) {
  return fetchWithAuth(`/avatars/${id}`, {
    method: "DELETE",
  });
}

// アバターに関連付けられた条件を取得
export async function getAvatarConditions(avatarId: number) {
  return fetchWithAuth(`/avatars/${avatarId}/conditions`);
}

// アバターに条件を関連付け
export async function addAvatarCondition(
  avatarId: number,
  conditionId: number,
) {
  return fetchWithAuth(`/avatars/${avatarId}/conditions/${conditionId}`, {
    method: "POST",
  });
}

// アバターから条件の関連付けを削除
export async function removeAvatarCondition(
  avatarId: number,
  conditionId: number,
) {
  return fetchWithAuth(`/avatars/${avatarId}/conditions/${conditionId}`, {
    method: "DELETE",
  });
}

// ==================== 健康心理条件管理 ====================
export async function getConditions(params?: {
  gender?: string;
  age_range?: string;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.gender) queryParams.append("gender", params.gender);
  if (params?.age_range) queryParams.append("age_range", params.age_range);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return fetchWithAuth(`/conditions${query ? `?${query}` : ""}`);
}

export async function getCondition(id: number) {
  return fetchWithAuth(`/conditions/${id}`);
}

export async function createCondition(data: {
  condition_data: any;
  gender: string;
  age_range: string;
}) {
  return fetchWithAuth("/conditions", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateCondition(
  id: number,
  data: {
    condition_data?: any;
    gender?: string;
    age_range?: string;
    version?: number;
  },
) {
  return fetchWithAuth(`/conditions/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteCondition(id: number) {
  return fetchWithAuth(`/conditions/${id}`, {
    method: "DELETE",
  });
}

// CSVエクスポート
export async function exportConditionsToCsv(params?: {
  gender?: string;
  age_range?: string;
}) {
  const queryParams = new URLSearchParams();
  if (params?.gender) queryParams.append("gender", params.gender);
  if (params?.age_range) queryParams.append("age_range", params.age_range);

  const query = queryParams.toString();
  const response = await fetch(
    `/api/admin/conditions/export/csv${query ? `?${query}` : ""}`,
    {
      credentials: "include",
    },
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "エラーが発生しました" }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `health_conditions_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// CSVインポート
export async function importConditionsFromCsv(
  file: File,
  gender: string,
  age_range: string,
) {
  const formData = new FormData();
  formData.append("csv", file);
  formData.append("gender", gender);
  formData.append("age_range", age_range);

  const response = await fetch("/api/admin/conditions/import/csv", {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "エラーが発生しました" }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }

  if (response.status === 204) return null;
  return response.json();
}

// ==================== 条件紐づき問診票・課題文 ====================

// 条件に紐づく問診票一覧
export async function getConditionQuestionnaires(conditionId: number) {
  return fetchWithAuth(`/conditions/${conditionId}/questionnaires`);
}

// 問診票一覧（全件・条件の性別/年代付き）
export async function getAllConditionQuestionnaires() {
  return fetchWithAuth(`/condition-questionnaires`);
}

// 問診票削除
export async function deleteConditionQuestionnaire(id: number) {
  return fetchWithAuth(`/condition-questionnaires/${id}`, { method: "DELETE" });
}

// 課題文一覧（全件・条件の性別/年代付き）
export async function getAllConditionInstructions() {
  return fetchWithAuth(`/condition-instructions`);
}

// 課題文削除
export async function deleteConditionInstruction(id: number) {
  return fetchWithAuth(`/condition-instructions/${id}`, { method: "DELETE" });
}

// 承認済み講座の表示順を更新
export async function updateConditionInstructionLectureOrder(
  courseCategoryId: number,
  orderedInstructionIds: number[],
) {
  return fetchWithAuth(`/condition-instructions/lecture-order`, {
    method: "PUT",
    body: JSON.stringify({ courseCategoryId, orderedInstructionIds }),
  });
}

// 条件から問診票をLLM生成
export async function generateConditionQuestionnaire(conditionId: number) {
  return fetchWithAuth(`/conditions/${conditionId}/generate-questionnaire`, {
    method: "POST",
  });
}

// 条件に紐づく課題文一覧
export async function getConditionInstructions(conditionId: number) {
  return fetchWithAuth(`/conditions/${conditionId}/instructions`);
}

// 条件から課題文をLLM生成
export async function generateConditionInstruction(
  conditionId: number,
  selectedCells?: Array<{
    rowIndex: number;
    colIndex: number;
    header: string;
    value: string;
  }>,
  lvSelections?: Record<string, number[]>,
) {
  const body: Record<string, unknown> = {};
  if (selectedCells && selectedCells.length > 0) {
    body.selected_cells = selectedCells;
  }
  if (lvSelections) {
    body.lv_selections = lvSelections;
  }
  return fetchWithAuth(`/conditions/${conditionId}/generate-instruction`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface InstructionVariantSelectionPayload {
  selected_cells: Array<{
    rowIndex: number;
    colIndex: number;
    header: string;
    value: string;
  }>;
  selected_level_keys: Record<string, string[]>;
  guidance_levels?: Array<"detailed" | "viewpoints" | "purpose_only">;
}

export async function previewConditionInstructionVariants(
  conditionId: number,
  payload: InstructionVariantSelectionPayload,
) {
  return fetchWithAuth(
    `/conditions/${conditionId}/preview-instruction-variants`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export async function previewConditionInstructionBriefing(
  conditionId: number,
  payload: {
    instruction_data: Record<string, unknown>;
    guidance_level?: string;
  },
) {
  return fetchWithAuth(
    `/conditions/${conditionId}/preview-instruction-briefing`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

export async function saveConditionInstructionVariants(
  conditionId: number,
  payload: InstructionVariantSelectionPayload & {
    instruction_data: Record<string, unknown>;
    selected_guidance_level: "detailed" | "viewpoints" | "purpose_only";
    instruction_id?: number;
    review_status?: "draft" | "in_review" | "approved";
  },
) {
  return fetchWithAuth(`/conditions/${conditionId}/save-instruction-variants`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// 問診票1件取得
export async function getConditionQuestionnaire(id: number) {
  return fetchWithAuth(`/condition-questionnaires/${id}`);
}

// 問診票更新
export async function updateConditionQuestionnaire(
  id: number,
  questionnaire_data: object,
) {
  return fetchWithAuth(`/condition-questionnaires/${id}`, {
    method: "PUT",
    body: JSON.stringify({ questionnaire_data }),
  });
}

// 問診票レビューステータス更新
export async function updateConditionQuestionnaireReviewStatus(
  id: number,
  status: "approved" | "draft" | "in_review",
) {
  return fetchWithAuth(`/condition-questionnaires/${id}/review`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

// 課題文1件取得
export async function getConditionInstruction(id: number) {
  return fetchWithAuth(`/condition-instructions/${id}`);
}

// 公開講座から、教員が安全に編集できる下書きコピーを作成
export async function copyConditionInstruction(id: number) {
  return fetchWithAuth(`/condition-instructions/${id}/copy`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// 課題文更新
export async function updateConditionInstruction(
  id: number,
  instruction_data: object,
) {
  return fetchWithAuth(`/condition-instructions/${id}`, {
    method: "PUT",
    body: JSON.stringify({ instruction_data }),
  });
}

// 面接前の読み上げ文だけ更新（課題文・セル対応・承認状態は変更しない）
export async function updateConditionInstructionBriefingScript(
  id: number,
  briefingScript: string,
) {
  return fetchWithAuth(`/condition-instructions/${id}/briefing-script`, {
    method: "PATCH",
    body: JSON.stringify({ briefing_script: briefingScript }),
  });
}

// 課題文レビューステータス更新
export async function updateConditionInstructionReviewStatus(
  id: number,
  status: "approved" | "draft" | "in_review",
) {
  return fetchWithAuth(`/condition-instructions/${id}/review`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

// ==================== LLM設定管理 ====================
export async function getLlms(params?: {
  search?: string;
  provider?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.provider) queryParams.append("provider", params.provider);
  if (params?.is_active !== undefined)
    queryParams.append("is_active", params.is_active.toString());
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return fetchWithAuth(`/llms${query ? `?${query}` : ""}`);
}

export async function getLlm(id: number) {
  return fetchWithAuth(`/llms/${id}`);
}

export async function createLlm(data: {
  provider: string;
  model_name: string;
  parameters: any;
  is_active?: boolean;
}) {
  return fetchWithAuth("/llms", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateLlm(
  id: number,
  data: {
    provider?: string;
    model_name?: string;
    parameters?: any;
    is_active?: boolean;
  },
) {
  return fetchWithAuth(`/llms/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteLlm(id: number) {
  return fetchWithAuth(`/llms/${id}`, {
    method: "DELETE",
  });
}

export async function toggleLlmActive(id: number) {
  return fetchWithAuth(`/llms/${id}/toggle-active`, {
    method: "PATCH",
  });
}

// ==================== LLM割り当て管理 ====================
export async function getLlmAssignments() {
  return fetchWithAuth("/llm-assignments");
}

export async function updateLlmAssignment(
  purpose: string,
  data: {
    llm_id: number | null;
    temperature: number | null;
    max_tokens: number | null;
  },
) {
  return fetchWithAuth(`/llm-assignments/${encodeURIComponent(purpose)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ==================== プロンプト管理 ====================
export async function getPrompts(params?: {
  search?: string;
  prompt_type?: string;
  exclude_type?: string;
  avatar_id?: number;
  condition_id?: number;
  questionnaire_id?: number;
  llm_id?: number;
  is_active?: boolean;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.prompt_type)
    queryParams.append("prompt_type", params.prompt_type);
  if (params?.exclude_type)
    queryParams.append("exclude_type", params.exclude_type);
  if (params?.avatar_id)
    queryParams.append("avatar_id", params.avatar_id.toString());
  if (params?.condition_id)
    queryParams.append("condition_id", params.condition_id.toString());
  if (params?.questionnaire_id)
    queryParams.append("questionnaire_id", params.questionnaire_id.toString());
  if (params?.llm_id) queryParams.append("llm_id", params.llm_id.toString());
  if (params?.is_active !== undefined)
    queryParams.append("is_active", params.is_active.toString());
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return fetchWithAuth(`/prompts${query ? `?${query}` : ""}`);
}

export async function getPrompt(id: number) {
  return fetchWithAuth(`/prompts/${id}`);
}

export async function createPrompt(data: {
  name: string;
  prompt_type?: string;
  prompt_text: string;
  description?: string;
  template?: string;
  avatar_id?: number;
  condition_id?: number;
  llm_id?: number;
  is_active?: boolean;
}) {
  return fetchWithAuth("/prompts", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updatePrompt(
  id: number,
  data: {
    name?: string;
    prompt_type?: string;
    prompt_text?: string;
    description?: string;
    template?: string;
    avatar_id?: number;
    condition_id?: number;
    llm_id?: number;
    is_active?: boolean;
  },
) {
  return fetchWithAuth(`/prompts/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deletePrompt(id: number) {
  return fetchWithAuth(`/prompts/${id}`, {
    method: "DELETE",
  });
}

export async function togglePromptActive(id: number) {
  return fetchWithAuth(`/prompts/${id}/toggle-active`, {
    method: "PATCH",
  });
}

// ==================== 面接管理 ====================
export async function getInterviews(params?: {
  search?: string;
  user_id?: number;
  prompt_id?: number;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.user_id) queryParams.append("user_id", params.user_id.toString());
  if (params?.prompt_id)
    queryParams.append("prompt_id", params.prompt_id.toString());
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return fetchWithAuth(`/interviews${query ? `?${query}` : ""}`);
}

export async function getInterview(id: number) {
  return fetchWithAuth(`/interviews/${id}`);
}

export async function deleteInterview(id: number) {
  return fetchWithAuth(`/interviews/${id}`, {
    method: "DELETE",
  });
}

// ==================== スコア管理 ====================
export async function getScores(params?: {
  user_id?: number;
  interview_id?: number;
  score_type?: string;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.user_id) queryParams.append("user_id", params.user_id.toString());
  if (params?.interview_id)
    queryParams.append("interview_id", params.interview_id.toString());
  if (params?.score_type) queryParams.append("score_type", params.score_type);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return fetchWithAuth(`/scores${query ? `?${query}` : ""}`);
}

export async function getScore(id: number) {
  return fetchWithAuth(`/scores/${id}`);
}

export async function deleteScore(id: number) {
  return fetchWithAuth(`/scores/${id}`, {
    method: "DELETE",
  });
}

// ==================== 履歴管理 ====================
export async function getHistories(params?: {
  user_id?: number;
  action_type?: string;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.user_id) queryParams.append("user_id", params.user_id.toString());
  if (params?.action_type)
    queryParams.append("action_type", params.action_type);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.limit) queryParams.append("limit", params.limit.toString());

  const query = queryParams.toString();
  return fetchWithAuth(`/histories${query ? `?${query}` : ""}`);
}

export async function getHistory(id: number) {
  return fetchWithAuth(`/histories/${id}`);
}

// ==================== TTS読み方辞書管理 ====================
export async function getTtsPronunciationDictionary() {
  return fetchWithAuth("/tts-pronunciation-dictionary");
}

export async function getTtsPronunciationDictionaryEntry(id: number) {
  return fetchWithAuth(`/tts-pronunciation-dictionary/${id}`);
}

export async function createTtsPronunciationDictionaryEntry(data: {
  original_text: string;
  pronunciation_text: string;
  description?: string;
}) {
  return fetchWithAuth("/tts-pronunciation-dictionary", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateTtsPronunciationDictionaryEntry(
  id: number,
  data: {
    original_text: string;
    pronunciation_text: string;
    description?: string;
  },
) {
  return fetchWithAuth(`/tts-pronunciation-dictionary/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteTtsPronunciationDictionaryEntry(id: number) {
  return fetchWithAuth(`/tts-pronunciation-dictionary/${id}`, {
    method: "DELETE",
  });
}

// ==================== STT誤認識修正辞書管理 ====================
export async function getSttCorrectionDictionary() {
  return fetchWithAuth("/stt-correction-dictionary");
}

export async function getSttCorrectionDictionaryEntry(id: number) {
  return fetchWithAuth(`/stt-correction-dictionary/${id}`);
}

export async function createSttCorrectionDictionaryEntry(data: {
  misrecognized_text: string;
  correct_text: string;
  description?: string;
  priority?: number;
}) {
  return fetchWithAuth("/stt-correction-dictionary", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateSttCorrectionDictionaryEntry(
  id: number,
  data: {
    misrecognized_text: string;
    correct_text: string;
    description?: string;
    priority?: number;
  },
) {
  return fetchWithAuth(`/stt-correction-dictionary/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteSttCorrectionDictionaryEntry(id: number) {
  return fetchWithAuth(`/stt-correction-dictionary/${id}`, {
    method: "DELETE",
  });
}

// ==================== STT設定管理 ====================
export async function getSttSettings() {
  return fetchWithAuth("/stt-settings");
}

export async function getSttSetting(key: string) {
  return fetchWithAuth(`/stt-settings/${key}`);
}

export async function updateSttSetting(
  key: string,
  data: { value: string | number; description?: string },
) {
  return fetchWithAuth(`/stt-settings/${key}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ==================== 専門用語管理 ====================
export async function getTechnicalTerms() {
  return fetchWithAuth("/technical-terms");
}

export async function getTechnicalTerm(id: number) {
  return fetchWithAuth(`/technical-terms/${id}`);
}

export async function createTechnicalTerm(data: {
  term: string;
  reading?: string;
  meaning?: string;
  category?: string;
  general_term?: string;
  priority?: number;
}) {
  return fetchWithAuth("/technical-terms", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateTechnicalTerm(
  id: number,
  data: {
    term: string;
    reading?: string;
    meaning?: string;
    category?: string;
    general_term?: string;
    priority?: number;
  },
) {
  return fetchWithAuth(`/technical-terms/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteTechnicalTerm(id: number) {
  return fetchWithAuth(`/technical-terms/${id}`, {
    method: "DELETE",
  });
}

// ===== Phrase List =====
export async function getPhraseList() {
  return fetchWithAuth("/phrase-list");
}

export async function createPhrase(phrase: string) {
  return fetchWithAuth("/phrase-list", {
    method: "POST",
    body: JSON.stringify({ phrase }),
  });
}

export async function deletePhrase(id: number) {
  return fetchWithAuth(`/phrase-list/${id}`, {
    method: "DELETE",
  });
}

// ==================== TTS キャッシュ（パフォーマンス） ====================

export interface TtsCacheDashboard {
  baseDir: string;
  diskBytesApprox: number;
  lifetimeHits: number;
  lifetimeStores: number;
  config: {
    maxDiskBytes: number;
    idleDays: number;
    evictionIntervalMs: number;
  } | null;
  processCounters: { hits: number; stores: number };
  /** false のとき DB 集計・設定が読めない（マイグレーション未適用など） */
  dbAvailable?: boolean;
  dbError?: string;
}

export async function getTtsCacheDashboard(): Promise<TtsCacheDashboard> {
  return fetchWithAuth("/tts-cache/dashboard");
}

export interface TtsCacheEntryRow {
  hash: string;
  textPreview: string;
  hitCount: number;
  approxBytes: number;
}

export interface TtsCacheEntriesResponse {
  dbAvailable?: boolean;
  dbError?: string;
  total: number;
  limit: number;
  offset: number;
  entries: TtsCacheEntryRow[];
}

export async function getTtsCacheEntries(params: {
  limit?: number;
  offset?: number;
}): Promise<TtsCacheEntriesResponse> {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.offset != null) sp.set("offset", String(params.offset));
  const q = sp.toString();
  return fetchWithAuth(`/tts-cache/entries${q ? `?${q}` : ""}`);
}

export async function clearAllTtsCache(): Promise<{
  ok: boolean;
  message?: string;
}> {
  return fetchWithAuth("/tts-cache/clear-all", { method: "POST" });
}

export async function clearTtsCacheStatsOnly(): Promise<{
  ok: boolean;
  message?: string;
}> {
  return fetchWithAuth("/tts-cache/clear-stats", { method: "POST" });
}

export async function runTtsCacheEviction(): Promise<{
  ok: boolean;
  result: {
    diskOrphansRemoved: number;
    dbOrphansRemoved: number;
    evictedCount: number;
    bytesFreedApprox: number;
  };
}> {
  return fetchWithAuth("/tts-cache/run-eviction", { method: "POST" });
}

export async function updateTtsCacheConfig(data: {
  maxDiskBytes?: number;
  idleDays?: number;
  evictionIntervalMs?: number;
}): Promise<{ ok: boolean; config: unknown }> {
  return fetchWithAuth("/tts-cache/config", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}
